"use client";
import Image from "next/image";

import { useState } from "react";
import Link from "next/link";
import CalculatorShell from "@/components/CalculatorShell";
import CitationSection from "@/components/CitationSection";
import InputField from "@/components/InputField";
import SelectField from "@/components/SelectField";
import CheckboxField from "@/components/CheckboxField";
import ResultCard from "@/components/ResultCard";
import { calcPaint, type PaintResult } from "@/lib/formulas";
import { CALCULATOR_SPECS } from "@/lib/calculator-info-data";
import FaqSection from "@/components/FaqSection";
import RelatedCalculators from "@/components/RelatedCalculators";

const SURFACE_OPTIONS = [
  { value: "normal", label: "Normal / Smooth (400 sq ft/gal)" },
  { value: "porous", label: "Porous / Textured (300 sq ft/gal)" },
  { value: "dark_to_light", label: "Dark to Light Color Change (300 sq ft/gal)" },
];

const COATS_OPTIONS = [
  { value: 1, label: "1 coat" },
  { value: 2, label: "2 coats (recommended)" },
  { value: 3, label: "3 coats (heavy color change)" },
];

export default function CalculatorClient() {
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [heightFt, setHeightFt] = useState("8");
  const [numDoors, setNumDoors] = useState("1");
  const [numWindows, setNumWindows] = useState("2");
  const [numCoats, setNumCoats] = useState(2);
  const [surfaceType, setSurfaceType] = useState<"normal" | "porous" | "dark_to_light">("normal");
  const [includeCeiling, setIncludeCeiling] = useState(false);
  const [result, setResult] = useState<PaintResult | null>(null);

  function handleCalculate() {
    const res = calcPaint({
      lengthFt: parseFloat(length) || 0,
      widthFt: parseFloat(width) || 0,
      heightFt: parseFloat(heightFt) || 8,
      numDoors: parseInt(numDoors) || 0,
      numWindows: parseInt(numWindows) || 0,
      numCoats,
      surfaceType,
      includeCeiling,
    });
    setResult(res);
  }

  return (
    <CalculatorShell
      name="Paint Calculator"
      description="Find out exactly how many gallons of paint you need for any room, with waste already factored in."
      icon="paint"
      category="diy"
      accentColor="#ec4899"
      hasResult={result !== null}
      onCalculate={handleCalculate}
      disclaimer="Highly porous walls or dramatic dark-to-light color changes may require additional coats — verify coverage rate before purchasing."
      proTip="Buy an extra quart for future touch-ups; paint colors can vary slightly between manufacturing batches."
      inputsPanel={
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <InputField id="paint-length" label="Room Length" unit="ft" type="number" min="0" step="0.5" placeholder="12" value={length} onChange={(e) => { setLength(e.target.value); setResult(null); }} />
            <InputField id="paint-width" label="Room Width" unit="ft" type="number" min="0" step="0.5" placeholder="10" value={width} onChange={(e) => { setWidth(e.target.value); setResult(null); }} />
          </div>
          <InputField id="paint-height" label="Wall Height" unit="ft" type="number" min="0" step="0.5" placeholder="8" value={heightFt} onChange={(e) => { setHeightFt(e.target.value); setResult(null); }} hint="Standard ceiling height is 8 ft" />

          <div className="grid grid-cols-2 gap-3">
            <InputField id="paint-doors" label="Doors" type="number" min="0" step="1" placeholder="1" value={numDoors} onChange={(e) => { setNumDoors(e.target.value); setResult(null); }} hint="~21 sq ft each" />
            <InputField id="paint-windows" label="Windows" type="number" min="0" step="1" placeholder="2" value={numWindows} onChange={(e) => { setNumWindows(e.target.value); setResult(null); }} hint="~15 sq ft each" />
          </div>

          <SelectField id="surface-type" label="Surface Type" value={surfaceType} onChange={(e) => { setSurfaceType(e.target.value as typeof surfaceType); setResult(null); }} options={SURFACE_OPTIONS} />
          <SelectField id="num-coats" label="Number of Coats" value={numCoats} onChange={(e) => { setNumCoats(parseInt(e.target.value)); setResult(null); }} options={COATS_OPTIONS} />

          <CheckboxField id="include-ceiling" label="Include ceiling" hint="Adds room length × width to the total area" checked={includeCeiling} onChange={(v) => { setIncludeCeiling(v); setResult(null); }} />
        </div>
      }
      resultsPanel={
        result && (
          <div className="space-y-3">
            <ResultCard
              label="Gallons Recommended (+ 10% buffer)"
              value={result.gallonsRecommended}
              unit="gallons"
              highlight
              size="lg"
              sub={`${result.gallonsExact.toFixed(2)} gallons exact · Coverage: ${result.coveragePerGallon} sq ft/gal`}
            />
            <div className="grid grid-cols-2 gap-3">
              <ResultCard label="Net Paintable Area" value={result.netPaintableArea} unit="sq ft" accent />
              <ResultCard label="Total Area Painted" value={result.totalArea} unit="sq ft" accent />
            </div>
            {result.ceilingArea > 0 && (
              <ResultCard label="Ceiling Area" value={result.ceilingArea} unit="sq ft" />
            )}
            {result.gallonsExact < 1 && (
              <div className="info-box">
                Less than 1 gallon needed — consider buying <strong>{result.quartsRecommended} quarts</strong> instead for better value.
              </div>
            )}
            {/* Breakdown of deductions */}
            <div className="pt-4 mt-2">
              <div className="font-mono text-[9px] font-bold uppercase tracking-wider text-[var(--ink-faint)] pb-2 select-none">
                Area Breakdown
              </div>
              <div className="flex flex-col border-b border-[var(--line)]">
                {[
                  { label: "Gross wall area", value: `${result.grossWallArea} sq ft` },
                  { label: "Minus doors & windows", value: `−${(result.grossWallArea - result.netPaintableArea - result.ceilingArea).toFixed(0)} sq ft` },
                  { label: "Net paintable walls", value: `${result.netPaintableArea} sq ft` },
                  ...(result.ceilingArea > 0 ? [{ label: "Ceiling", value: `${result.ceilingArea} sq ft` }] : []),
                ].map((row, i) => (
                  <div
                    key={i}
                    className="flex justify-between items-center py-2.5 px-3 border-t border-[var(--line)]"
                  >
                    <span className="text-[12.5px] font-sans font-semibold text-[var(--ink-soft)]">{row.label}</span>
                    <span className="font-mono text-[13px] text-[var(--ink)] font-bold">{row.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center text-[var(--ink)]">
        {/* Row 1 - Left: Buying Guide */}
        <div className="space-y-4 md:order-1">
          <h3 className="text-xl font-bold tracking-tight text-[var(--ink)]">
            Room Paint Selection & Buying Guide
          </h3>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Choosing the right paint sheen and <Link href="/articles/how-much-paint-do-you-need-per-room/" className="text-[var(--accent)] hover:underline font-semibold">estimating coverage</Link> is key to a professional finish. A standard gallon of quality paint covers approximately <strong>{CALCULATOR_SPECS.paint.buyingGuide.standardCoverage.value} sq ft</strong> on smooth surfaces, but drops to around <strong>{CALCULATOR_SPECS.paint.buyingGuide.porousCoverage.value} sq ft</strong> for porous or textured walls. Sheens matter: use Flat/Matte for ceilings to hide imperfections, Eggshell or Satin for living spaces, and Semi-Gloss or Gloss for high-moisture, high-traffic areas like bathrooms, kitchens, and baseboards to ensure washability. Upgrading colors from dark to light will always require an initial primer coat to prevent bleed-through.
          </p>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Coverage rates assume a reasonably smooth, sealed surface and proper roller loading. New drywall, repaired patches, masonry, raw wood, heavy texture, and very deep color changes can absorb more paint than the label suggests. Primer is not just an extra coat; it evens porosity so the finish coat dries with consistent sheen and color.
          </p>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Buy paint from the same product line and sheen for the whole room, and ask the store to box multiple gallons together when color uniformity matters. Keep the formula label and a small labeled touch-up container for future repairs because wall color can vary by batch, sheen, lighting, and aging.
          </p>
        </div>

        {/* Row 1 - Right: Buying Guide Image */}
        <div className="relative rounded-xl overflow-hidden border border-[var(--line)] aspect-[3/2] w-full bg-[var(--surface-muted)] md:order-2">
          <Image src="/images/paint_material_guide.webp" alt="Interior room design showing painted walls with satin finish and paint cans" fill sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 576px" className="object-cover" priority />
        </div>

        {/* Row 2 - Left: Site Prep Image */}
        <div className="relative rounded-xl overflow-hidden border border-[var(--line)] aspect-[3/2] w-full bg-[var(--surface-muted)] md:order-3">
          <Image src="/images/paint_site_prep.webp" alt="Room prepared for painting showing trim covered in painter tape and canvas drop cloths" fill sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 576px" className="object-cover" loading="lazy" />
        </div>

        {/* Row 2 - Right: Site Prep Guide */}
        <div className="space-y-4 md:order-4">
          <h3 className="text-xl font-bold tracking-tight text-[var(--ink)]">
            Room Paint Substrate Prep & Installation
          </h3>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            A flawless paint job is 80% surface preparation. Start by cleaning walls with a mild soap solution to remove oils and grease. Patch holes or dents with lightweight spackling compound, let it dry, and sand it completely flush. Apply high-quality painter&apos;s tape to baseboards and trim, sealing the edges firmly with a putty knife. For optimal paint adhesion and leveling, maintain room temperatures between <strong>{CALCULATOR_SPECS.paint.sitePrep.tempRangeMin.value}°F</strong> and <strong>{CALCULATOR_SPECS.paint.sitePrep.tempRangeMax.value}°F</strong>. Avoid painting in high humidity to prevent bubbling. If you are painting outside, follow our <Link href="/articles/cost-to-paint-house-exterior/" className="text-[var(--accent)] hover:underline font-semibold">exterior house painting guide</Link>.
          </p>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Inspect the surface under bright side lighting before opening paint. Glossy old finishes should be scuff-sanded, stains should be sealed with the correct primer, and mildew should be cleaned and corrected before coating. Paint will not hide nail pops, tape ridges, water stains, or failing previous coatings.
          </p>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Sequence matters: ceiling first, then walls, then trim. Cut in one wall at a time and roll while the edge is still wet to avoid picture-framing. Remove tape before the paint fully hardens so the film does not tear along the trim line.
          </p>
        </div>
      </div>

      <CitationSection
        standardCode={CALCULATOR_SPECS.paint.industryStandards.vocLimitStandard.standardCode}
        sourceName={CALCULATOR_SPECS.paint.industryStandards.vocLimitStandard.sourceName}
        sourceUrl={CALCULATOR_SPECS.paint.industryStandards.vocLimitStandard.sourceUrl}
        paragraphs={[
          <p key="p1">
            Volatile Organic Compounds (VOCs) are carbon-based chemicals that evaporate easily at room temperature. They are commonly found in traditional paints, solvents, and finishes, and can cause short-term respiratory irritation, headaches, and long-term indoor air quality degradation. Under environmental regulations evaluated by <b>{CALCULATOR_SPECS.paint.industryStandards.vocLimitStandard.standardCode}</b>, interior flat architectural coatings are restricted to a maximum VOC content of <b>{CALCULATOR_SPECS.paint.industryStandards.vocLimitStandard.value} g/L</b>. This cap protects installers and homeowners alike from high concentrations of chemical vapors during and immediately following application.
          </p>,
          <p key="p2">
            In recent years, regional regulations—such as those from California&apos;s SCAQMD or the multi-state OTC—have driven industry limits even lower, often capping VOCs at 50 g/L or less for non-flat sheens as well. Low-VOC and zero-VOC paint formulations are strongly recommended for bedrooms, nurseries, kitchens, and other confined spaces. It is worth noting that zero-VOC paints dry faster and may require a wet edge technique during application to prevent brush or roller marks, making good ventilation and climate control critical.
          </p>,
          <p key="p3">
            Paint estimates should distinguish wall paint, ceiling paint, trim enamel, primer, caulk, patching compound, sanding supplies, tape, rollers, brushes, trays, and drop cloths. The gallons number is only the finish-coat material, not the full project supply list.
          </p>,
          <p key="p4">
            Repaint timing should account for the existing coating. Peeling, chalking, glossy oil-based paint, smoke residue, water stains, and patched drywall all need different preparation steps before the finish coat can perform as intended.
          </p>
        ]}
        keyRequirements={[
          "Interior flat architectural coatings are restricted to a maximum VOC content of 50 g/L.",
          "Low-VOC and zero-VOC paint formulations are recommended for enclosed areas.",
          "Zero-VOC paints dry more rapidly and require a continuous wet edge technique.",
          "Good ventilation and room temperatures between 50°F and 90°F are critical during drying.",
          "Prime new drywall, repairs, stains, and major color changes before finish coats.",
          "Separate wall, ceiling, trim, primer, and prep supplies in the estimate.",
          "Maintain a wet edge and inspect under side lighting before final coat."
        ]}
      />

      <FaqSection calculatorId="paint" />
      <RelatedCalculators calculatorId="paint" />
    </CalculatorShell>
  );
}
